/**
 * Ícones PRÓPRIOS de item (Fase 10.2 reskin) — SVG procedural por tipo de arma / slot de
 * armadura, tingido pela raridade. Substitui os emoji por arte vetorial consistente
 * (leve, escala sem perda). Reflete a arma real (sword ≠ axe ≠ spear ≠ bow).
 */
import type { EquipmentInstance } from "./EquipmentTypes";
import { definitionById, isWeaponDef } from "./Catalog";
import { rarityCss } from "./Rarity";

const STEEL = "#c7cede";
const GRIP = "#241f38";

/** SVG por tipo de arma. `c` = cor principal (raridade); `e` = cor de energia/aresta. */
function weaponSvg(weaponType: string, c: string, e: string): string {
  switch (weaponType) {
    case "axe":
      return `<rect x="11.2" y="4" width="1.6" height="16.6" rx="0.8" fill="${GRIP}"/>
        <path d="M12 3.6 C18.6 3.6 21.2 9 20 13.4 C16.7 10.8 14.1 10.9 12 11.7 Z" fill="${c}"/>
        <path d="M12 5 C16.9 5.3 18.9 8.7 18.4 11.9 C16 10.3 14 10.5 12 11 Z" fill="${e}" opacity="0.5"/>
        <path d="M12 3.6 C7.4 3.8 5.9 5.8 6.4 8.3 C8.4 7.1 10.2 7.2 12 7.7 Z" fill="#4a4668"/>
        <circle cx="12" cy="3.4" r="1.2" fill="${e}"/>
        <rect x="10.7" y="20.4" width="2.6" height="1.6" rx="0.8" fill="${STEEL}"/>`;
    case "bow":
      return `<path d="M8 3 C15.6 6 15.6 18 8 21" fill="none" stroke="${c}" stroke-width="2.2" stroke-linecap="round"/>
        <path d="M8.7 4.2 C14 6.8 14 17.2 8.7 19.8" fill="none" stroke="${e}" stroke-width="0.8" opacity="0.6"/>
        <line x1="8" y1="3" x2="8" y2="21" stroke="#d8dae2" stroke-width="0.9" opacity="0.85"/>
        <line x1="8" y1="12" x2="18" y2="12" stroke="${e}" stroke-width="0.9"/>
        <path d="M18 12 L15.4 10.7 M18 12 L15.4 13.3" fill="none" stroke="${e}" stroke-width="0.9"/>
        <circle cx="8" cy="3.4" r="1" fill="${e}"/><circle cx="8" cy="20.6" r="1" fill="${e}"/>`;
    case "mace":
      return `<rect x="11.1" y="9" width="1.8" height="12.4" rx="0.9" fill="${GRIP}"/>
        <rect x="8" y="3.4" width="8" height="7" rx="1.4" fill="${c}"/>
        <path d="M8 4 L6 3 M16 4 L18 3 M8 9.8 L6 11 M16 9.8 L18 11 M12 3.4 L12 1.4" stroke="${STEEL}" stroke-width="1.4" stroke-linecap="round"/>
        <circle cx="12" cy="6.9" r="1.6" fill="${e}"/>
        <rect x="10.6" y="20.6" width="2.8" height="1.5" rx="0.7" fill="${STEEL}"/>`;
    case "staff":
      return `<rect x="11.2" y="6" width="1.7" height="15.6" rx="0.85" fill="${GRIP}"/>
        <path d="M12 2.2 C15.4 2.2 17 5 17 7.2 C17 9.9 14.8 11.6 12 11.6 C9.2 11.6 7 9.9 7 7.2 C7 5 8.6 2.2 12 2.2 Z" fill="none" stroke="${STEEL}" stroke-width="1.1"/>
        <circle cx="12" cy="7.1" r="3" fill="${c}"/>
        <circle cx="12" cy="7.1" r="1.5" fill="${e}"/>
        <rect x="10.7" y="20.5" width="2.6" height="1.5" rx="0.7" fill="${STEEL}"/>`;
    case "tome": // 10.9L25 — grimório: livro fechado com fivela + runa brilhando
      return `<rect x="5" y="4" width="14" height="16.4" rx="1.4" fill="${GRIP}"/>
        <rect x="5.8" y="4.8" width="12.4" height="14.8" rx="1" fill="${c}"/>
        <line x1="12" y1="4.8" x2="12" y2="19.6" stroke="${GRIP}" stroke-width="0.9" opacity="0.6"/>
        <path d="M12 8.6 L14 12.2 L12 15.8 L10 12.2 Z" fill="${e}"/>
        <rect x="5" y="4" width="14" height="1.4" rx="0.7" fill="${STEEL}"/>`;
    case "crossbow": // 10.9L25 — besta compacta: trilho reto + arco curto travado + corda
      return `<rect x="4" y="11.2" width="16" height="1.6" rx="0.8" fill="${GRIP}"/>
        <path d="M7 6 C10.4 8.4 10.4 15.6 7 18" fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round"/>
        <path d="M17 6 C13.6 8.4 13.6 15.6 17 18" fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round"/>
        <line x1="7" y1="6" x2="17" y2="6" stroke="#d8dae2" stroke-width="0.8" opacity="0.85"/>
        <line x1="7" y1="18" x2="17" y2="18" stroke="#d8dae2" stroke-width="0.8" opacity="0.85"/>
        <rect x="10.6" y="12" width="2.8" height="8.6" rx="0.8" fill="${STEEL}"/>
        <circle cx="12" cy="12" r="1.1" fill="${e}"/>`;
    default: // sword
      return `<path d="M12 1.4 L13.9 4 L13 15 L11 15 L10.1 4 Z" fill="${c}"/>
        <line x1="12" y1="2.6" x2="12" y2="15" stroke="${e}" stroke-width="0.8" opacity="0.9"/>
        <path d="M12 1.4 L13.9 4 L12.6 4 Z" fill="#ffffff" opacity="0.5"/>
        <rect x="7.5" y="15" width="9" height="1.9" rx="0.9" fill="${STEEL}"/>
        <circle cx="7.8" cy="15.9" r="1" fill="${e}"/><circle cx="16.2" cy="15.9" r="1" fill="${e}"/>
        <rect x="11.1" y="16.7" width="1.8" height="4" rx="0.9" fill="${GRIP}"/>
        <circle cx="12" cy="21" r="1.1" fill="${e}"/>`;
  }
}

/** SVG por slot de armadura. */
function armorSvg(slot: string, c: string, e: string): string {
  switch (slot) {
    case "head":
      return `<path d="M6 12 C6 6 18 6 18 12 L18 14 L6 14 Z" fill="${c}"/><rect x="6" y="13" width="12" height="2.4" rx="1" fill="#4a4668"/><rect x="10.5" y="7" width="3" height="7" rx="1.2" fill="${e}"/>`;
    case "chest":
      return `<path d="M5 6 L12 8 L19 6 L18 17 C15 20 9 20 6 17 Z" fill="${c}"/><path d="M12 8 L12 19" stroke="#2a2740" stroke-width="1"/><circle cx="12" cy="12" r="1.8" fill="${e}"/>`;
    case "gloves":
      return `<path d="M8 8 L14 8 L15 14 C15 18 9 18 9 14 Z" fill="${c}"/><rect x="9" y="5.5" width="1.6" height="3.5" rx="0.8" fill="${c}"/><rect x="11.2" y="5" width="1.6" height="4" rx="0.8" fill="${c}"/><rect x="8" y="14.5" width="7" height="2" rx="1" fill="${e}"/>`;
    case "boots":
      return `<path d="M9 4 L13 4 L13 14 L18 14 L18 18 L9 18 Z" fill="${c}"/><rect x="8.5" y="17" width="10" height="2.2" rx="1" fill="#4a4668"/><rect x="9.5" y="8" width="3" height="1.6" fill="${e}"/>`;
    default: // accessory (anel/gema)
      return `<circle cx="12" cy="14" r="5.2" fill="none" stroke="${c}" stroke-width="2.4"/><path d="M12 3 L15 7 L12 9 L9 7 Z" fill="${e}"/>`;
  }
}

/** Retorna o SVG (string) do ícone do item, tingido pela raridade. */
export function itemSvg(inst: EquipmentInstance): string {
  const def = definitionById(inst.definitionId);
  const c = rarityCss(inst.rarity);
  const e = "#bff4ff"; // energia ciano viva (arestas/gemas)
  const inner = def && isWeaponDef(def) ? weaponSvg(def.weaponType, c, e) : armorSvg(def ? def.slot : "accessory", c, e);
  // brilho por raridade (mais forte em itens raros lê melhor no grid)
  return `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block"><g style="filter:drop-shadow(0 0 1.1px ${c})">${inner}</g></svg>`;
}

/** 10.6G — ícone só do TIPO de arma, numa cor dada. Usado nas etiquetas de fraqueza que
 *  aparecem na barra de vida do monstro. */
export function weaponTypeSvg(weaponType: string, color = "#ffffff"): string {
  return `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block">${weaponSvg(weaponType, color, color)}</svg>`;
}

/** Ícone genérico por slot vazio (placeholder do próprio slot, ainda vetorial). */
export function slotSvg(slot: string): string {
  const c = "#6b6f86";
  const e = "#8a90a8";
  const inner = slot === "weapon" ? weaponSvg("sword", c, e) : armorSvg(slot, c, e);
  return `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block;opacity:0.55">${inner}</svg>`;
}
